/**
 * Version Check Hook
 *
 * Handles version checking, WebSocket updates, and forced update logic
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { getLatestVersion, markVersionSeen } from '@/routes/api/main-api';
import { useAuth } from '@/store';

// Get build-time constants with proper fallbacks
// Use the variables defined in vite.config.ts
const APP_VERSION = import.meta.env.VITE_APP_VERSION;

const BUILD_ID =
  import.meta.env.BUILD_ID ||
  import.meta.env.VITE_BUILD_ID ||
  `dev-${new Date().toISOString().split('T')[0]}`;

// Debug environment variables in development
if (import.meta.env.DEV) {
  console.debug('[useVersionCheck] Environment variables:', {
    'import.meta.env.APP_VERSION': import.meta.env.APP_VERSION,
    'import.meta.env.VITE_APP_VERSION': import.meta.env.VITE_APP_VERSION,
    'import.meta.env.BUILD_ID': import.meta.env.BUILD_ID,
    'import.meta.env.VITE_BUILD_ID': import.meta.env.VITE_BUILD_ID,
    'Final APP_VERSION': APP_VERSION,
    'Final BUILD_ID': BUILD_ID,
  });
}

interface VersionData {
  _id: string;
  version: string;
  buildId: string;
  isForced: boolean;
  hasSeenVersion: boolean; // Required field from backend
  changelog: Array<{
    type: 'fix' | 'new feature';
    title: string;
    media?: string[];
  }>;
  publishedAt: string;
}

interface UseVersionCheckOptions {
  /**
   * Polling interval in milliseconds (default: 60000ms = 1 minute)
   */
  pollingInterval?: number;

  /**
   * Whether to enable WebSocket updates (default: true)
   */
  enableWebSocket?: boolean;

  /**
   * WebSocket URL (optional, will try to detect from current location)
   */
  wsUrl?: string;

  /**
   * Whether to check version on mount (default: true)
   */
  checkOnMount?: boolean;

  /**
   * Whether to enable automatic polling (default: false)
   */
  enablePolling?: boolean;
}

interface UseVersionCheckReturn {
  /**
   * Whether there's a new version available
   */
  hasUpdate: boolean;

  /**
   * The latest version data
   */
  latestVersion: VersionData | null;

  /**
   * Whether the update is forced
   */
  isForced: boolean;

  /**
   * Current app version
   */
  currentVersion: string;

  /**
   * Current build ID
   */
  currentBuildId: string;

  /**
   * Whether version check is in progress
   */
  isChecking: boolean;

  /**
   * Any error that occurred during version check
   */
  error: string | null;

  /**
   * Manually trigger version check
   */
  checkVersion: () => Promise<void>;

  /**
   * Mark current version as seen
   */
  markAsSeen: () => Promise<void>;

  /**
   * Trigger app update (reload with cache bust)
   */
  updateApp: () => void;

  /**
   * Dismiss the update (only if not forced)
   */
  dismissUpdate: () => void;
}

export function useVersionCheck(
  options: UseVersionCheckOptions = {},
): UseVersionCheckReturn {
  const {
    pollingInterval = 120000, // 2 minutes (increased to avoid rate limiting)
    enableWebSocket = false, // Disabled by default since server may not have WebSocket endpoint
    wsUrl,
    checkOnMount = false, // Disabled by default - manual check only
    enablePolling = false, // Disabled by default - no automatic polling
  } = options;

  // Get user info for marking version as seen
  const { user, isAuth } = useAuth();
  const isAuthenticated = isAuth && !!user; // Check both auth status and user existence
  const userId = user?._id || 'me'; // Fallback to 'me' if no user ID

  // State
  const [hasUpdate, setHasUpdate] = useState(false);
  const [latestVersion, setLatestVersion] = useState<VersionData | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);

  // Refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const wsRef = useRef<WebSocket | undefined>(undefined);
  const hasMarkedSeenRef = useRef(false);
  const retryCountRef = useRef(0);
  const lastCheckTimeRef = useRef(0);

  /**
   * Check for version updates
   */
  const checkVersion = useCallback(async () => {
    // Don't check if user is not authenticated
    if (!isAuthenticated) {
      return;
    }

    if (isChecking) return;

    // Rate limiting: Don't check more than once every 30 seconds
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckTimeRef.current;
    if (timeSinceLastCheck < 30000) {
      return;
    }

    setIsChecking(true);
    setError(null);
    lastCheckTimeRef.current = now;

    try {

      const response = await getLatestVersion();

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch latest version');
      }

      const latest = response.data;

      if (!latest) {
        setHasUpdate(false);
        setLatestVersion(null);
        retryCountRef.current = 0; // Reset retry count on success
        return;
      }


      setLatestVersion(latest);
      retryCountRef.current = 0; // Reset retry count on success

      // Check if there's a new version OR if user hasn't seen the current latest version
      const versionDiffers = latest.version !== APP_VERSION;
      const notSeenYet = latest.hasSeenVersion === false;
      const needsUpdate = versionDiffers || notSeenYet;

      setHasUpdate(needsUpdate && !updateDismissed);

      // Don't auto-mark as seen anymore - let user interaction handle it
      if (needsUpdate) {
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';

      // Handle rate limiting specifically
      if (
        errorMessage.includes('Too Many Requests') ||
        errorMessage.includes('429')
      ) {
        retryCountRef.current++;
        const backoffDelay = Math.min(
          30000 * Math.pow(2, retryCountRef.current),
          300000,
        ); // Max 5 minutes

        console.warn(
          `⚠️ [useVersionCheck] Rate limited (attempt ${retryCountRef.current}), backing off for ${backoffDelay / 1000}s`,
        );

        // Don't show error toast for rate limiting, just log it
        setError(
          `Rate limited - will retry in ${Math.round(backoffDelay / 1000)} seconds`,
        );

        // Schedule next check with exponential backoff
        setTimeout(() => {
          if (retryCountRef.current > 0) {
            checkVersion();
          }
        }, backoffDelay);

        return;
      }

      console.error('❌ [useVersionCheck] Version check failed:', errorMessage);
      setError(errorMessage);

      // Show toast for network errors, but not for rate limiting or other API errors
      if (errorMessage.includes('Network error')) {
        toast.error('Failed to check for updates', {
          description: 'Please check your internet connection',
        });
      }
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, updateDismissed, isAuthenticated]);

  /**
   * Mark version as seen
   */
  const markAsSeen = useCallback(async () => {
    if (!latestVersion || hasMarkedSeenRef.current) return;

    try {
      await markVersionSeen({ userId, version: latestVersion.version });
      hasMarkedSeenRef.current = true;

      // Update local state to reflect that user has seen the version
      setLatestVersion((prev) =>
        prev ? { ...prev, hasSeenVersion: true } : prev,
      );
      setHasUpdate(false); // Hide notification after marking as seen

    } catch (err) {
      console.error(
        '❌ [useVersionCheck] Failed to mark version as seen:',
        err,
      );
      // Don't show error toast for this, it's not critical
    }
  }, [latestVersion, userId]);

  /**
   * Update the app (reload with cache bust)
   */
  const updateApp = useCallback(() => {

    // Clear any cached data
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    // Reload with cache bust using build ID
    const buildId = latestVersion?.buildId || BUILD_ID;
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('v', buildId);

    window.location.href = currentUrl.toString();
  }, [latestVersion]);

  /**
   * Dismiss the update (only if not forced)
   */
  const dismissUpdate = useCallback(() => {
    if (latestVersion?.isForced) {
      console.warn('⚠️ [useVersionCheck] Cannot dismiss forced update');
      return;
    }

    setUpdateDismissed(true);
    setHasUpdate(false);
  }, [latestVersion]);

  /**
   * Setup WebSocket connection for real-time updates
   */
  const setupWebSocket = useCallback(() => {
    if (!enableWebSocket || !isAuthenticated) return;

    try {
      // Determine WebSocket URL
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const url = wsUrl || `${protocol}//${host}/ws`;


      const ws = new WebSocket(url);
      wsRef.current = ws;

      // Set a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'new-version') {
            checkVersion();
          }
        } catch (err) {
          console.error(
            '❌ [useVersionCheck] WebSocket message parse error:',
            err,
          );
        }
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);

        // Only log if it was an unexpected close
        if (event.code !== 1000) {
        }

        // Only attempt reconnection if WebSocket is still enabled and it wasn't a manual close
        if (enableWebSocket && event.code !== 1000) {
          // Exponential backoff for reconnection (max 30 seconds)
          const reconnectDelay = Math.min(
            5000 * Math.pow(2, Math.random()),
            30000,
          );

          setTimeout(() => {
            if (
              enableWebSocket &&
              wsRef.current?.readyState !== WebSocket.OPEN
            ) {
              setupWebSocket();
            }
          }, reconnectDelay);
        }
      };

      ws.onerror = () => {
        clearTimeout(connectionTimeout);
        // Don't log WebSocket errors as they're expected when the endpoint doesn't exist
        // The system will gracefully fall back to polling
      };
    } catch {
    }
  }, [enableWebSocket, wsUrl, checkVersion, isAuthenticated]);

  /**
   * Setup polling for version checks
   */
  const setupPolling = useCallback(() => {
    // Don't setup polling if disabled
    if (!enablePolling) {
      return;
    }

    // Don't setup polling if user is not authenticated
    if (!isAuthenticated) {
      return;
    }

    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Setup new interval
    pollingIntervalRef.current = setInterval(checkVersion, pollingInterval);

  }, [checkVersion, pollingInterval, isAuthenticated, enablePolling]);

  // Initialize version checking
  useEffect(() => {
    if (checkOnMount && isAuthenticated) {
      checkVersion();
    }

    // Setup WebSocket if enabled
    setupWebSocket();

    // Setup polling
    setupPolling();

    // Cleanup
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [
    checkOnMount,
    checkVersion,
    setupWebSocket,
    setupPolling,
    isAuthenticated,
  ]);

  // Reset dismissed state when version changes
  useEffect(() => {
    if (latestVersion && latestVersion.version !== APP_VERSION) {
      setUpdateDismissed(false);
      hasMarkedSeenRef.current = false;
    }
  }, [latestVersion]);

  // Re-check version when authentication status changes
  // DISABLED: Automatic check after login removed - manual check only
  // useEffect(() => {
  //   if (isAuthenticated) {
  //     checkVersion();
  //   }
  // }, [isAuthenticated, checkVersion]);

  // Stop version checking when user logs out
  useEffect(() => {
    if (!isAuthenticated) {

      // Clear polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = undefined;
      }

      // Close WebSocket connection
      if (wsRef.current) {
        wsRef.current.close(1000, 'User logged out'); // 1000 = normal closure
        wsRef.current = undefined;
      }

      // Reset state
      setHasUpdate(false);
      setLatestVersion(null);
      setIsChecking(false);
      setError(null);
      setUpdateDismissed(false);
      hasMarkedSeenRef.current = false;
      retryCountRef.current = 0;
      lastCheckTimeRef.current = 0;
    }
  }, [isAuthenticated]);

  return {
    hasUpdate,
    latestVersion,
    isForced: latestVersion?.isForced || false,
    currentVersion: APP_VERSION || '1.0.0',
    currentBuildId: BUILD_ID,
    isChecking,
    error,
    checkVersion,
    markAsSeen,
    updateApp,
    dismissUpdate,
  };
}
